import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Package, Plus, Search, ArrowUpDown, Trash2, X, AlertTriangle, Warehouse, ArrowRightLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = {
  panel: { label: 'Paneles', icon: '☀', color: '#D4A017' },
  inverter: { label: 'Inversores', icon: '⚡', color: '#3B82F6' },
  cable: { label: 'Cable', icon: '🔌', color: '#10B981' },
  structure: { label: 'Estructura', icon: '🏗', color: '#8B5CF6' },
  connector: { label: 'Conectores', icon: '🔗', color: '#06B6D4' },
  meter: { label: 'Contadores', icon: '📊', color: '#F59E0B' },
  protection: { label: 'Protecciones', icon: '🛡', color: '#EF4444' },
  tool: { label: 'Herramientas', icon: '🔧', color: '#64748B' },
  consumable: { label: 'Consumibles', icon: '📦', color: '#A3A3A3' },
  other: { label: 'Otros', icon: '📋', color: '#78716C' },
};

const UNITS = ['unidad', 'metro', 'kg', 'rollo', 'caja', 'pallet', 'juego', 'lote'];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState({ category: '', warehouse: '', search: '', lowStock: false });
  const [showCreate, setShowCreate] = useState(false);
  const [showMovement, setShowMovement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: 'panel', name: '', sku: '', brand: '', model: '', unit: 'unidad', warehouseId: '', quantityAvailable: 0, quantityMinimum: 0, unitCost: '', supplier: '' });
  const [movForm, setMovForm] = useState({ movementType: 'entry', quantity: '', projectId: '', reference: '', notes: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [it, wh, st, pr] = await Promise.all([
        api.get('/inventory/items'), api.get('/inventory/warehouses'),
        api.get('/inventory/stats'), api.get('/projects'),
      ]);
      setItems(it.data); setWarehouses(wh.data); setStats(st.data);
      setProjects(pr.data.data || pr.data || []);
    } catch (e) {} finally { setLoading(false); }
  };

  const filtered = items.filter(i => {
    if (filter.category && i.category !== filter.category) return false;
    if (filter.warehouse && i.warehouse_id !== filter.warehouse) return false;
    if (filter.lowStock && !(i.quantity_available <= i.quantity_minimum && i.quantity_minimum > 0)) return false;
    if (filter.search && !`${i.name} ${i.sku} ${i.brand} ${i.model}`.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  const createItem = async () => {
    if (!form.name) return toast.error('Nombre requerido');
    try {
      await api.post('/inventory/items', form);
      toast.success('Item creado'); setShowCreate(false); load();
      setForm({ category: 'panel', name: '', sku: '', brand: '', model: '', unit: 'unidad', warehouseId: '', quantityAvailable: 0, quantityMinimum: 0, unitCost: '', supplier: '' });
    } catch (e) { toast.error('Error'); }
  };

  const createMovement = async () => {
    if (!movForm.quantity || movForm.quantity <= 0) return toast.error('Cantidad requerida');
    try {
      await api.post('/inventory/movements', { itemId: showMovement.id, ...movForm, quantity: parseFloat(movForm.quantity) });
      toast.success('Movimiento registrado'); setShowMovement(null); load();
      setMovForm({ movementType: 'entry', quantity: '', projectId: '', reference: '', notes: '' });
    } catch (e) { toast.error('Error'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('¿Desactivar este item?')) return;
    try { await api.delete(`/inventory/items/${id}`); load(); toast.success('Desactivado'); } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div><h1><Package size={24} /> Inventario</h1><p className="subtitle">Gestión de almacén y stock</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Nuevo Item</button>
      </div>

      {/* Stats */}
      <div className="inv-stats">
        <div className="inv-stat-card"><span className="inv-stat-val">{stats.total_items || 0}</span><span className="inv-stat-lbl">Items activos</span></div>
        <div className="inv-stat-card warn"><span className="inv-stat-val">{stats.low_stock || 0}</span><span className="inv-stat-lbl">Stock bajo</span></div>
        <div className="inv-stat-card"><span className="inv-stat-val">{Number(stats.total_value || 0).toLocaleString('es', { style: 'currency', currency: 'EUR' })}</span><span className="inv-stat-lbl">Valor total</span></div>
        <div className="inv-stat-card"><span className="inv-stat-val">{stats.categories || 0}</span><span className="inv-stat-lbl">Categorías</span></div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input"><Search size={14} /><input placeholder="Buscar..." value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))} /></div>
        <select className="filter-select" value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}>
          <option value="">Todas categorías</option>
          {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <select className="filter-select" value={filter.warehouse} onChange={e => setFilter(f => ({ ...f, warehouse: e.target.value }))}>
          <option value="">Todos almacenes</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label className="inv-low-stock-toggle">
          <input type="checkbox" checked={filter.lowStock} onChange={e => setFilter(f => ({ ...f, lowStock: e.target.checked }))} />
          <AlertTriangle size={12} /> Stock bajo
        </label>
      </div>

      {/* Items Table */}
      <div className="inv-table-wrap">
        <table className="inv-table">
          <thead>
            <tr>
              <th>Cat.</th><th>Nombre</th><th>SKU</th><th>Marca/Modelo</th><th>Almacén</th>
              <th style={{ textAlign: 'right' }}>Stock</th><th style={{ textAlign: 'right' }}>Mín.</th>
              <th style={{ textAlign: 'right' }}>Coste ud.</th><th style={{ textAlign: 'right' }}>Valor</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(i => {
              const cat = CATEGORIES[i.category] || CATEGORIES.other;
              const isLow = i.quantity_minimum > 0 && i.quantity_available <= i.quantity_minimum;
              return (
                <tr key={i.id} className={isLow ? 'low-stock-row' : ''}>
                  <td><span className="inv-cat-dot" style={{ background: cat.color }}>{cat.icon}</span></td>
                  <td className="inv-name-cell"><span className="inv-item-name">{i.name}</span></td>
                  <td className="mono">{i.sku || '-'}</td>
                  <td className="inv-brand">{[i.brand, i.model].filter(Boolean).join(' ') || '-'}</td>
                  <td>{i.warehouse_name || '-'}</td>
                  <td className={`inv-qty ${isLow ? 'low' : ''}`}>{Number(i.quantity_available).toLocaleString('es')} {i.unit}</td>
                  <td className="inv-qty-min">{i.quantity_minimum > 0 ? Number(i.quantity_minimum).toLocaleString('es') : '-'}</td>
                  <td className="inv-cost">{i.unit_cost ? `${Number(i.unit_cost).toFixed(2)} €` : '-'}</td>
                  <td className="inv-cost">{i.unit_cost ? `${(i.quantity_available * i.unit_cost).toFixed(2)} €` : '-'}</td>
                  <td className="inv-actions">
                    <button className="btn-icon" title="Movimiento" onClick={() => setShowMovement(i)}><ArrowRightLeft size={14} /></button>
                    <button className="btn-icon" title="Eliminar" onClick={() => deleteItem(i.id)}><Trash2 size={14} style={{ color: '#EF4444' }} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={10} className="inv-empty">Sin items</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nuevo Item</h3><button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Categoría</label><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>{Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
                <div className="form-group"><label>Unidad</label><select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>{UNITS.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
              </div>
              <div className="form-group"><label>Nombre *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Panel Solar 550W JA Solar" /></div>
              <div className="form-row">
                <div className="form-group"><label>SKU</label><input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} /></div>
                <div className="form-group"><label>Marca</label><input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} /></div>
                <div className="form-group"><label>Modelo</label><input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Almacén</label><select value={form.warehouseId} onChange={e => setForm(f => ({ ...f, warehouseId: e.target.value }))}><option value="">Sin asignar</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                <div className="form-group"><label>Proveedor</label><input value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Cantidad inicial</label><input type="number" value={form.quantityAvailable} onChange={e => setForm(f => ({ ...f, quantityAvailable: e.target.value }))} /></div>
                <div className="form-group"><label>Stock mínimo</label><input type="number" value={form.quantityMinimum} onChange={e => setForm(f => ({ ...f, quantityMinimum: e.target.value }))} /></div>
                <div className="form-group"><label>Coste unitario (€)</label><input type="number" step="0.01" value={form.unitCost} onChange={e => setForm(f => ({ ...f, unitCost: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button><button className="btn btn-primary" onClick={createItem}>Crear Item</button></div>
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {showMovement && (
        <div className="modal-overlay" onClick={() => setShowMovement(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Movimiento: {showMovement.name}</h3><button className="btn-icon" onClick={() => setShowMovement(null)}><X size={18} /></button></div>
            <div className="modal-body">
              <p className="inv-mov-stock">Stock actual: <strong>{Number(showMovement.quantity_available).toLocaleString('es')} {showMovement.unit}</strong></p>
              <div className="form-row">
                <div className="form-group"><label>Tipo</label><select value={movForm.movementType} onChange={e => setMovForm(f => ({ ...f, movementType: e.target.value }))}>
                  <option value="entry">📥 Entrada</option><option value="exit">📤 Salida</option>
                  <option value="return">↩️ Devolución</option><option value="adjustment">⚖️ Ajuste</option>
                </select></div>
                <div className="form-group"><label>Cantidad *</label><input type="number" step="0.01" value={movForm.quantity} onChange={e => setMovForm(f => ({ ...f, quantity: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Proyecto (destino/origen)</label><select value={movForm.projectId} onChange={e => setMovForm(f => ({ ...f, projectId: e.target.value }))}>
                <option value="">Sin proyecto</option>{projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
              </select></div>
              <div className="form-group"><label>Referencia (albarán, PO...)</label><input value={movForm.reference} onChange={e => setMovForm(f => ({ ...f, reference: e.target.value }))} /></div>
              <div className="form-group"><label>Notas</label><textarea rows={2} value={movForm.notes} onChange={e => setMovForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowMovement(null)}>Cancelar</button><button className="btn btn-primary" onClick={createMovement}>Registrar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
